import React, { useEffect, useState } from 'react';
import { Button } from '../../../design-system';
import countries from '../../../constants/countries';
import { candidateRequest, takeInvitationToken, clearInvitationToken } from './prejoiningApi';
import { personalFields, permitFields, residencyField } from './prejoiningFields';
import { prejoiningWarnings } from './prejoiningWarnings';
import './prejoining.scss';

export default function PrejoiningPage() {
    const [token, setToken] = useState(takeInvitationToken);
    const [form, setForm] = useState(null);
    const [answers, setAnswers] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [invalid, setInvalid] = useState([]);
    const [notice, setNotice] = useState('');
    const [dirty, setDirty] = useState(false);
    const warnings = prejoiningWarnings(answers);
    const residencyCountry = form?.joiningDetailsReady
        ? form.employmentType === 'FREELANCER' ? answers.addressCountry : form.employmentCountry : null;
    const countryName = countries.find(country => country.code === residencyCountry)?.name || residencyCountry;
    useEffect(() => {
        const replaceInvitation = () => {
            if (!window.location.hash) return;
            const nextToken = takeInvitationToken();
            if (nextToken === token) return;
            setToken(nextToken); setForm(null); setAnswers({}); setDirty(false); setError(''); setNotice(''); setInvalid([]);
        };
        window.addEventListener('hashchange', replaceInvitation);
        return () => window.removeEventListener('hashchange', replaceInvitation);
    }, [token]);
    useEffect(() => {
        let active = true;
        document.title = 'Pre-joining form · Mediatropy';
        candidateRequest(token).then(data => {
            if (!active) return;
            setForm(data); setAnswers(data.answers || {});
            if (data.status === 'SUBMITTED') clearInvitationToken();
        }).catch(e => { if (active) setError(e.message); });
        return () => { active = false; };
    }, [token]);
    useEffect(() => {
        const warn = event => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);
    useEffect(() => {
        if (!error) return;
        const fieldId = invalid[0] === 'document' ? 'identity-upload' : invalid[0];
        const target = fieldId ? document.getElementById(fieldId) : document.getElementById('prejoining-error');
        target?.focus(); target?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }, [error, invalid]);
    function change(key, value) {
        setAnswers(previous => {
            const next = { ...previous, [key]: value,
                ...(key === 'documentType' && value === 'PASSPORT' ? { documentNoExpiry: false } : {}),
                ...(key === 'residency' ? { residencyCountry } : {}) };
            if (key === 'addressCountry' && value !== previous.addressCountry && form.employmentType === 'FREELANCER') {
                for (const field of ['residency', 'residencyCountry', 'permitPending', ...permitFields.map(([name]) => name)]) delete next[field];
            }
            return next;
        });
        setDirty(true); setNotice(''); setInvalid(previous => previous.filter(item => item !== key));
    }
    async function save(submit = false) {
        setBusy(true); setError(''); setInvalid([]); setNotice('');
        try {
            const data = await candidateRequest(token, submit ? '/submit' : '/draft', {
                method: submit ? 'POST' : 'PUT', body: { answers, revision: form.revision },
            });
            setForm(previous => ({ ...previous, ...data })); setDirty(false);
            if (submit) { setAnswers({}); clearInvitationToken(); }
            else { setAnswers(data.answers); setNotice('Progress saved. You can return using the link in your email.'); }
        } catch (e) { setError(e.message); setInvalid(e.fields || []); }
        finally { setBusy(false); }
    }
    async function upload(event) {
        const file = event.target.files?.[0];
        if (!file) return;
        event.target.value = '';
        if (file.size > 10 * 1024 * 1024) { setError('Please choose a file of up to 10 MB.'); return; }
        setBusy(true); setError('');
        try {
            const body = new FormData(); body.append('document', file); body.append('revision', form.revision);
            const data = await candidateRequest(token, '/document', { method: 'POST', body });
            setForm(previous => ({ ...previous, ...data })); setInvalid(previous => previous.filter(key => key !== 'document'));
            setNotice('Document uploaded. Remember to save or submit your answers.');
        } catch (e) { setError(e.message); }
        finally { setBusy(false); }
    }
    function field([key, label, type, maxLength]) {
        if (key === 'documentExpiryDate' && answers.documentNoExpiry) return null;
        const options = type === 'country' ? countries.map(country => [country.code, country.name]) : Array.isArray(type) ? type : null;
        const optional = ['addressPostcode', 'documentIssueDate'].includes(key);
        const warning = warnings[key];
        const props = { id: key, name: key, value: answers[key] || '', 'aria-invalid': invalid.includes(key),
            'aria-describedby': warning ? `${key}-warning` : invalid.includes(key) ? `${key}-error` : undefined,
            'data-warning': warning ? 'true' : undefined,
            ...(key === 'residency' ? { disabled: !residencyCountry } : {}),
            onChange: event => change(key, event.target.value) };
        return <div className="prejoining-field" key={key}>
            <label htmlFor={key}>{label}{optional ? ' (if applicable)' : ' *'}</label>
            {options ? <select {...props}><option value="">Select…</option>{options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}</select>
                : <input {...props} type={type} maxLength={maxLength} autoComplete={key === 'documentNumber' ? 'off' : undefined} />}
            {key === 'documentType' && answers.documentType === 'NIC' && <label className="prejoining-check"><input type="checkbox" checked={!!answers.documentNoExpiry} onChange={event => change('documentNoExpiry', event.target.checked)} /> My identity card has no expiry date</label>}
            {warning && <span className="prejoining-field-warning" id={`${key}-warning`} role="alert">{warning}</span>}
            {invalid.includes(key) && !warning && <span className="prejoining-field-error" id={`${key}-error`}>Please complete or check this field.</span>}
        </div>;
    }
    return <main className="prejoining-page" data-private="true">
        <header className="prejoining-header"><img src="/assets/images/card/logofront.svg" alt="Mediatropy" /><span>Pre-joining form</span></header>
        <section className="prejoining-card">
            <p className="prejoining-eyebrow">WELCOME TO MEDIATROPY</p>
            {form?.status === 'SUBMITTED' ? <div role="status"><h1>Thank you — your details are with HR.</h1>
                <p>You can close this page. Please contact your HR representative if you need to correct anything.</p></div>
                : <><h1>Let’s get ready for your joining.</h1>
                    <p>Complete your personal details and upload your identity document. Our HR team will review your answers before preparing your employee profile.</p>
                    {error && <div className="prejoining-alert" id="prejoining-error" tabIndex={-1} role="alert">{error}</div>}
                    {!form && !error && <p role="status">Opening your private form…</p>}
                    {form && <form onSubmit={event => { event.preventDefault(); save(true); }} noValidate>
                        {form.joiningDetailsReady ? <div className="prejoining-joining-details">
                            <p>Job title: <strong>{form.jobTitle}</strong></p>
                            {form.startDate && <p>Start date: <strong>{form.startDate.split('-').reverse().join('/')}</strong></p>}
                            {form.departmentName && <p>Department: <strong>{form.departmentName}</strong></p>}
                            {form.managerName && <p>Report to: <strong>{form.managerName}</strong></p>}
                            <p>Office: <strong>{form.officeName}</strong> · Employment type: <strong>{form.employmentType === 'FREELANCER' ? 'Freelancer' : 'Full time'}</strong></p>
                        </div> : <p role="status" className="prejoining-alert">HR needs to confirm your job title, office and employment type. Please contact HR for an updated invitation. You can still save your personal details.</p>}
                        <p className="prejoining-help">Fields marked * are required. You can save your progress and return from your invitation email.</p>
                        <fieldset disabled={busy}><legend>Personal details</legend>
                            <div className="prejoining-grid">{personalFields.map(field)}</div>
                            <div className="prejoining-upload"><label htmlFor="identity-upload">NIC / passport upload *</label>
                                <p>PDF, JPG or PNG · up to 10 MB</p>
                                {form.document && <p>Uploaded: <strong>{form.document.name}</strong></p>}
                                <input id="identity-upload" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={upload} aria-invalid={invalid.includes('document')} />
                            </div>
                        </fieldset>
                        <fieldset disabled={busy || !residencyCountry}><legend>Residency and work authorisation</legend>
                            {form.joiningDetailsReady && <p>{form.employmentType === 'FREELANCER'
                                ? residencyCountry ? 'Please answer for your home address country.' : 'Select your home address country above to answer the residency question.'
                                : `Please answer for the country of your ${form.officeName} office.`}</p>}
                            <div className="prejoining-grid">{field([residencyField[0], countryName ? `Residency in ${countryName}` : 'Residency', residencyField[2]])}</div>
                            {residencyCountry && answers.residency && answers.residency !== 'CITIZEN' && <>
                                <label className="prejoining-check"><input type="checkbox" checked={!!answers.permitPending} onChange={event => change('permitPending', event.target.checked)} /> Permit not yet issued / not applicable — HR will follow up</label>
                                {!answers.permitPending && <div className="prejoining-grid">{permitFields.map(field)}</div>}
                            </>}
                        </fieldset>
                        <p className="prejoining-help">Please check your answers before submitting. After submission, HR can send you a new link if a correction is needed.</p>
                        {notice && <p role="status">{notice}</p>}
                        <div className="prejoining-actions"><Button type="button" variant="secondary" disabled={busy} onClick={() => save(false)} uppercase={false}>Save progress</Button>
                            <Button type="submit" loading={busy} disabled={!form.joiningDetailsReady || !residencyCountry} uppercase={false}>Submit to HR</Button></div>
                    </form>}
                </>}
        </section>
        <footer className="prejoining-footer">Your private link opens this form only. For help, contact the HR representative who invited you.</footer>
    </main>;
}
